<app-modal
  [title]="'admin.customers.edit.data.title' | transloco"
  [subtitle]="'admin.customers.edit.data.subtitle' | transloco"
  [errorMessage]="errorMessage() || null"
  [isSubmitting]="isLoading()"
  [isDisabled]="customerForm.invalid"
  [confirmLabel]="'admin.customers.edit.data.confirmLabel' | transloco"
  [submittingLabel]="'shared.actions.saving' | transloco"
  (confirm)="onSubmit()"
  (cancelled)="cancel()"
  icon="bi bi-building"
>
  <form [formGroup]="customerForm" (ngSubmit)="onSubmit()">
    <!-- Nome + E-mail -->
    <div class="row mb-3 gy-3">
      <!-- Nome -->
      <div class="col-12 col-md-6">
        <label for="nome" class="form-label text-secondary-brand fw-semibold">
          {{ 'admin.customers.edit.nameLabel' | transloco }}
        </label>

        <div class="input-group">
          <span class="input-group-text border-brand">
            <i class="bi bi-building"></i>
          </span>

          <input
            id="nome"
            type="text"
            class="form-control form-control-md"
            [class.is-invalid]="isFieldInvalid('nome')"
            formControlName="nome"
            [placeholder]="'admin.customers.edit.nameLabel' | transloco"
            [attr.maxlength]="MAX_NAME_LENGTH"
            autocomplete="organization"
          />
        </div>

        @if (isFieldInvalid('nome')) {
          <div class="invalid-feedback d-block">
            <i class="bi bi-exclamation-circle me-1"></i>

            @if (customerForm.get('nome')?.errors?.['required']) {
              {{ 'shared.fieldRequired' | transloco }}
            } @else if (customerForm.get('nome')?.errors?.['minlength']) {
              {{
                'validation.minLength'
                  | transloco
                    : {
                        min: customerForm.get('nome')?.errors?.['minlength']?.requiredLength,
                      }
              }}
            } @else if (customerForm.get('nome')?.errors?.['maxlength']) {
              {{
                'validation.maxLength'
                  | transloco
                    : {
                        max: customerForm.get('nome')?.errors?.['maxlength']?.requiredLength,
                      }
              }}
            }
          </div>
        }

        <small class="text-secondary-brand float-end">
          {{ customerForm.get('nome')?.value?.length ?? 0 }}/{{ MAX_NAME_LENGTH }}
        </small>
      </div>

      <!-- E-mail -->
      <div class="col-12 col-md-6">
        <label for="email" class="form-label text-secondary-brand fw-semibold">
          {{ 'admin.customers.edit.emailLabel' | transloco }}
        </label>

        <div class="input-group">
          <span class="input-group-text border-brand">
            <i class="bi bi-envelope"></i>
          </span>

          <input
            id="email"
            type="email"
            class="form-control form-control-md"
            [class.is-invalid]="isFieldInvalid('email')"
            formControlName="email"
            [placeholder]="'shared.placeholders.businessEmail' | transloco"
            [attr.maxlength]="MAX_EMAIL_LENGTH"
            autocomplete="email"
          />
        </div>

        @if (isFieldInvalid('email')) {
          <div class="invalid-feedback d-block">
            <i class="bi bi-exclamation-circle me-1"></i>

            @if (customerForm.get('email')?.errors?.['required']) {
              {{ 'shared.fieldRequired' | transloco }}
            } @else if (customerForm.get('email')?.errors?.['email']) {
              {{ 'validation.email' | transloco }}
            } @else if (customerForm.get('email')?.errors?.['maxlength']) {
              {{
                'validation.maxLength'
                  | transloco
                    : {
                        max: customerForm.get('email')?.errors?.['maxlength']?.requiredLength,
                      }
              }}
            }
          </div>
        }

        <small class="text-secondary-brand float-end">
          {{ customerForm.get('email')?.value?.length ?? 0 }}/{{ MAX_EMAIL_LENGTH }}
        </small>
      </div>
    </div>

    <!-- CNPJ + Telefone + Status -->
    <div class="row mb-3 gy-3">
      <!-- CNPJ -->
      <div [class]="isAdmin() ? 'col-12 col-md-6' : 'col-12 col-md-4'">
        <label for="cnpj" class="form-label text-secondary-brand fw-semibold d-flex justify-content-between">
          <span>{{ 'admin.customers.edit.cnpjLabel' | transloco }}</span>

          @if (isCnpjLocked()) {
            <i
              class="bi bi-info-circle-fill"
              style="color: #8c8d8f"
              [ngbTooltip]="'admin.customers.edit.defaultCnpjLockedTooltip' | transloco"
              tooltipClass="custom-tooltip"
              placement="top"
              container="body"
            >
            </i>
          }
        </label>

        <div class="input-group">
          <span class="input-group-text border-brand">
            <i [class]="isCnpjLocked() ? 'bi bi-lock-fill' : 'bi bi-card-text'"></i>
          </span>

          <input
            id="cnpj"
            type="text"
            class="form-control form-control-md"
            [class.is-invalid]="isFieldInvalid('cnpj')"
            [class.field-locked]="isCnpjLocked()"
            [readOnly]="isCnpjLocked()"
            [attr.aria-readonly]="isCnpjLocked() ? 'true' : null"
            formControlName="cnpj"
            mask="00.000.000/0000-00"
            [dropSpecialCharacters]="false"
            [placeholder]="'shared.placeholders.cnpj' | transloco"
            autocomplete="off"
          />
        </div>

        @if (isFieldInvalid('cnpj')) {
          <div class="invalid-feedback d-block">
            <i class="bi bi-exclamation-circle me-1"></i>

            @if (customerForm.get('cnpj')?.errors?.['required']) {
              {{ 'shared.fieldRequired' | transloco }}
            } @else if (customerForm.get('cnpj')?.errors?.['pattern']) {
              {{ 'validation.cnpjFormat' | transloco }}
            } @else if (customerForm.get('cnpj')?.errors?.['maxlength']) {
              {{
                'validation.maxLength'
                  | transloco
                    : {
                        max: customerForm.get('cnpj')?.errors?.['maxlength']?.requiredLength,
                      }
              }}
            }
          </div>
        }
      </div>

      <!-- Telefone -->
      <div [class]="isAdmin() ? 'col-12 col-md-6' : 'col-12 col-md-4'">
        <label for="telefone" class="form-label text-secondary-brand fw-semibold">
          {{ 'admin.customers.edit.phoneLabel' | transloco }}
        </label>

        <div class="input-group">
          <span class="input-group-text border-brand">
            <i class="bi bi-telephone"></i>
          </span>

          <input
            id="telefone"
            type="text"
            class="form-control form-control-md"
            [class.is-invalid]="isFieldInvalid('telefone')"
            formControlName="telefone"
            mask="(00) 00000-0000"
            [dropSpecialCharacters]="false"
            [placeholder]="'shared.placeholders.phone' | transloco"
            autocomplete="tel"
          />
        </div>

        @if (isFieldInvalid('telefone')) {
          <div class="invalid-feedback d-block">
            <i class="bi bi-exclamation-circle me-1"></i>

            @if (customerForm.get('telefone')?.errors?.['pattern']) {
              {{ 'validation.phoneFormat' | transloco }}
            } @else if (customerForm.get('telefone')?.errors?.['maxlength']) {
              {{
                'validation.maxLength'
                  | transloco
                    : {
                        max: customerForm.get('telefone')?.errors?.['maxlength']?.requiredLength,
                      }
              }}
            }
          </div>
        }
      </div>

      <!-- Status -->
      @if (!isAdmin()) {
        <div class="col-12 col-md-4">
          <label for="isActive" class="form-label text-secondary-brand fw-semibold">
            {{ 'admin.customers.edit.statusLabel' | transloco }}
          </label>

          <ng-select
            id="isActive"
            [items]="statusOptions"
            bindValue="value"
            [clearable]="false"
            formControlName="isActive"
            (change)="onStatusChange()"
          >
            <ng-template ng-label-tmp let-item="item">
              {{ item.labelKey | transloco }}
            </ng-template>

            <ng-template ng-option-tmp let-item="item">
              {{ item.labelKey | transloco }}
            </ng-template>
          </ng-select>

          @if (statusAlterado() && customerForm.get('isActive')?.value === 'false') {
            <div class="alert alert-warning mt-2">
              <strong>
                <i class="bi bi-exclamation-triangle me-1"></i>
                {{ 'shared.actions.confirm' | transloco }}!
              </strong>

              <br />

              {{ 'admin.customers.edit.inactivationWarning' | transloco }}
            </div>
          }
        </div>
      }
    </div>

    <!-- Tier contratado (apenas Super) -->
    @if (isSuper()) {
      <div class="row mb-3 gy-3">
        <div class="col-12">
          <label for="contractedTier" class="form-label text-secondary-brand fw-semibold">
            {{ 'admin.customers.edit.contractedTierLabel' | transloco }}
          </label>

          <ng-select
            id="contractedTier"
            [items]="activeTiers()"
            bindValue="id"
            bindLabel="name"
            [clearable]="true"
            [placeholder]="'admin.customers.edit.contractedTierPlaceholder' | transloco"
            formControlName="contractedTierId"
          />

          <div class="form-text text-secondary-brand">
            {{ 'admin.customers.edit.contractedTierHelp' | transloco }}
          </div>
        </div>
      </div>
    }
  </form>
</app-modal>
